import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import dateFormat from 'date-fns/format';
import { get as _get, has as _has } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';

import { AutoPayUtilService } from '@amfam/billing/auto-pay/feature';
import { AutoPayUtilSelectors } from '@amfam/billing/auto-pay/util';
import { BillAccountsSelectors, BillingUtilService } from '@amfam/billing/billaccount/data-access';
import { PolicySelectors } from '@amfam/policy/data-access';
import { AnalyticsActions, AnalyticsFacade, EventAnalyticParams } from '@amfam/shared/analytics';
import {
  BillAccount,
  BillingAnalytics,
  BillingPaymentConfirmationModel,
  BillingPaymentPaths
} from '@amfam/shared/models';
import { CurrencyUSDPipe } from '@amfam/shared/ui/pipes';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import {
  Applications,
  ApplicationService,
  CopyService,
  UtilService
} from '@amfam/shared/utility/shared-services';
import { ConfirmationModel, ErrorObj } from '@amfam/ui-kit';

// Store
import { billingSharedSelectors } from '../../+state/common.selectors';
import { AutopayCardData } from '../autopay-card/autopay-card.model';

@Component({
  selector: 'ds-billing-payment-confirmation',
  templateUrl: './billing-payment-confirmation.component.html',
  styleUrls: ['./billing-payment-confirmation.component.scss'],
  standalone: false
})
export class BillingPaymentConfirmationComponent implements OnInit, OnDestroy {
  @Input() billingConfirmationData: BillingPaymentConfirmationModel;

  confirmationData: ConfirmationModel;
  columns = [{}];
  rows = [{}];
  paymentTerm = 'Minimum Due';
  cssClasses = 'material noshadow expandable payment-automatic';

  public autopayErrorList$: Observable<ErrorObj[]>;
  public matchingbillaccount: BillAccount;
  public deleteScheduled: boolean;
  public hideAutopayCard: boolean;
  public showAutopayCard: boolean;
  public showPaperlessCard: boolean;
  public isEligibleBillAccountForAutoPay: boolean;
  public isEligibleToGoPaperless$: Observable<boolean>;
  public policyNumber: string;

  public messageHeader: string;
  public confirmationDetails: string;
  public allDone: string;

  public confirmationSubHeading: string;
  public cancelPath: string;
  public isFuturePaymentSupported: boolean;
  public hasClassicPolicies$: Observable<boolean>;
  public hasAnyAdvancedPolicyForBillAccount$: Observable<boolean>;
  public clickFuturePaymentsLink = BillingAnalytics.clickFuturePaymentsLink;
  public isAdminApp: boolean = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN);
  public billingDetailPath: string;

  private eventAmountArr: Array<string> = [];
  private paymentMethodConfirmation: boolean;
  private stop$ = new Subject<void>();

  // **** Start of Analytics data for this component

  // eventAnalytic for scheduling payment
  private eventAnalytic: EventAnalyticParams = {
    eventName: 'BillingPayments:Payment',
    eventStep: 'complete',
    eventAmount: this.eventAmountArr || [''],
    eventTerm: ['']
  };

  // eventAnalytic for adding auto pay.
  private eventAutoPayAnalytic: EventAnalyticParams = {
    eventName: 'Automatic Payments',
    eventStep: 'complete',
    eventAmount: this.eventAmountArr || [''],
    eventTerm: ['']
  };
  // **** End of Analytics data

  private confirmationSubHeadingAmount: string;
  private confirmationSubHeadingDate: string;

  constructor(
    private copyService: CopyService,
    private billingUtilService: BillingUtilService,
    private currencyUSDPipe: CurrencyUSDPipe = new CurrencyUSDPipe(),
    private store: Store,
    private analyticsFacade: AnalyticsFacade,
    private utilService: UtilService,
    private featureFlagService: FeatureFlagService,
    private applicationService: ApplicationService,
    private autoPayUtil: AutoPayUtilService
  ) {}

  ngOnInit() {
    this.deleteScheduled = this.billingConfirmationData.subCategory === 'deleteScheduled';
    this.paymentMethodConfirmation = this.billingConfirmationData.category === 'paymentMethod';

    this.messageHeader = this.copyService.getCopy(
      `billing.${this.billingConfirmationData.confirmationMsgHeading}`,
      null,
      this.deleteScheduled
    );

    this.confirmationDetails = this.copyService.get('billing', 'confirmationDetails');
    this.allDone = this.copyService.get('billing', 'allDone');

    this.columns = this.billingConfirmationData.columnNamePropMap;
    this.rows = this.billingConfirmationData.rowsPropValueMap;

    if (
      _get(this.billingConfirmationData, 'paymentTerm[0]', '') !== 'otherAmount' &&
      _get(this.billingConfirmationData, 'paymentTerm[0]', '') !== 'minAmountDue'
    ) {
      this.paymentTerm = 'Account Balance';
    }

    // Send the bill account payment amounts to analytics when available
    if (_has(this.billingConfirmationData, 'billAccounts')) {
      this.billingConfirmationData.billAccounts.forEach(billAccount => {
        this.eventAmountArr.push(billAccount.billAcctPaymentAmount);
      });
    }

    /* SK: Workaround to avoid can not read property error.
     billingConfirmationData being sent from the payment service...
     this component is rendering before all elements/nodes avaialble and causes paymentTerm undefined.
     Set eventTerm once we have paymentTerm
     */
    if (_has(this.billingConfirmationData, 'paymentTerm')) {
      this.eventAutoPayAnalytic.eventTerm = this.billingConfirmationData.paymentTerm;
      this.eventAnalytic.eventTerm = this.billingConfirmationData.paymentTerm;
    }
    this.setAnalytics();

    // Add confirmation subtext of $X.XX will be paid on MM/DD/YYYY for scheduled payments and edited payments
    if (_has(this.billingConfirmationData, 'subCategory')) {
      switch (this.billingConfirmationData.subCategory) {
        case 'scheduled':
        case 'editScheduled':
          this.confirmationSubHeadingAmount = this.currencyUSDPipe.transform(
            this.billingConfirmationData.amount
          );
          this.confirmationSubHeadingDate = dateFormat(
            this.billingConfirmationData.date,
            'MM/DD/YY'
          );
          this.confirmationSubHeading =
            this.confirmationSubHeadingAmount +
            ' will be paid on ' +
            this.confirmationSubHeadingDate;
          break;
        case 'pastDue':
          this.confirmationSubHeading = '';
          this.billingConfirmationData.billAccounts.forEach(billAccount => {
            if (billAccount.pastDueInfo.currentAmountDue > 0) {
              this.confirmationSubHeading += `You still have ${this.currencyUSDPipe.transform(
                billAccount.pastDueInfo.currentAmountDue
              )} to be paid by ${dateFormat(
                billAccount.pastDueInfo.currentAmountDueDate,
                'MM/DD/YYYY'
              )} to stay current on ${billAccount.billAccountNickName}<br>`;
            }
          });
          break;
        default:
          break;
      }
    }

    // Create general confirmation data object
    if (this.rows && this.rows.length > 0) {
      this.confirmationData = {
        confirmation: {
          heading: this.messageHeader,
          subHeading: this.confirmationSubHeading
        },
        details: [
          {
            heading: this.confirmationDetails,

            tableRows: this.rows,
            tableColumns: this.columns
          }
        ]
      };

      this.store
        .select(
          createSelector(
            BillAccountsSelectors.selectBillAccounts,
            billingSharedSelectors.selectBillaccountsListEligibleForAutopay,
            AutoPayUtilSelectors.selectBillAccountsEligibleForAutoPay,
            PolicySelectors.selectPolicies,
            BrandSelectors.selectIsPartner,
            (
              billaccounts,
              _oldBillAccountsEligibleForAutoPay,
              billAccountsEligibleForAutoPay,
              policies,
              isPartner
            ) => ({
              billaccounts,
              eligibleBillAccts: billAccountsEligibleForAutoPay,
              policies,
              isPartner
            })
          )
        )
        .pipe(take(1))
        .subscribe(state => {
          this.isFuturePaymentSupported = !state.isPartner;
          this.matchingbillaccount = state.billaccounts.find(
            billaccount =>
              billaccount.billingPreferences.accountNickName ===
              _get(this.confirmationData, 'details[0].tableRows[0].account')
          );

          this.showPaperlessCard = this.billingConfirmationData.category === 'scheduledPayments';

          this.isEligibleBillAccountForAutoPay = state.eligibleBillAccts.some(
            eligibleAccount =>
              eligibleAccount.billAccountNumber ===
              _get(this.matchingbillaccount, 'billAccountNumber')
          );

          this.showAutopayCard =
            _get(this.confirmationData, 'details[0].tableRows.length', 0) === 1 &&
            !_get(this.confirmationData, 'details[0].tableRows[0].matchingAutoPay', false) &&
            this.showPaperlessCard &&
            !!this.matchingbillaccount &&
            this.paymentTerm &&
            !this.deleteScheduled &&
            this.isEligibleBillAccountForAutoPay;

          this.hasAnyAdvancedPolicyForBillAccount$ = this.store.select(
            billingSharedSelectors.selectIsAnyAdvancedPoliciyInGivenPolicies,
            { dataPolicies: _get(this.matchingbillaccount, 'policyList', []) }
          );
        });
    }

    this.isEligibleToGoPaperless$ = this.store
      .select(billingSharedSelectors.selectIsEligibleToGoPaperless)
      .pipe(take(1));

    this.billingDetailPath = this.paymentMethodConfirmation
      ? ''
      : `${BillingPaymentPaths.BILLING_SECTION_PAGE}/${this.matchingbillaccount.billAccountNumber}`;

    this.cancelPath = `${BillingPaymentPaths.BILLING_SECTION_PAGE}`;

    this.policyNumber = _get(this.matchingbillaccount, 'policyList[0].policyNumber');
  }

  returnToBillingandPayments() {
    /**
     * @description If we have a cancel path set use that else navigate
     */
    this.store
      .select(BillAccountsSelectors.selectBillAccountCancelPath)
      .pipe(take(1))
      .subscribe(cancelPath => {
        this.store.dispatch(
          fromRouterActions.Go({
            path: cancelPath ? [cancelPath] : [BillingPaymentPaths.BILLING_SECTION_PAGE]
          })
        );
      });
  }

  autopaySignup(autopayPreferencePayload: AutopayCardData) {
    this.autoPayUtil.addAutoPayQuick(
      autopayPreferencePayload.billaccountNickName,
      autopayPreferencePayload.paymentMethod
    );
  }

  autopayCustomize() {
    if (!this.isAdminApp) {
      this.analyticsFacade.trackButtonClick(BillingAnalytics.clickAutoPayCustomize);
    }
    // AUTOPAY ENTRY POINT
    this.autoPayUtil.addAutoPay(
      this.matchingbillaccount.billAccountNumber,
      `/billing/${this.matchingbillaccount.billAccountNumber}`
    );
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }

  private setAnalytics() {
    if (_has(this.billingConfirmationData, 'subCategory')) {
      switch (this.billingConfirmationData.subCategory) {
        case 'scheduled':
        case 'multipleScheduled':
        case 'noAccounts':
          if (!this.isAdminApp) {
            this.analyticsFacade.trackPageAndEvent(
              BillingAnalytics.pageSchedulePaymentComplete,
              this.eventAnalytic
            );
          } else {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageSchedulePaymentCompleteAdmin,
                  event: this.eventAnalytic
                }
              })
            );
          }
          break;
        case 'paymentWithAutoPay':
          if (!this.isAdminApp) {
            this.analyticsFacade.trackPageAndEvent(
              BillingAnalytics.pageAddAutoPayComplete,
              this.eventAutoPayAnalytic
            );
            this.analyticsFacade.trackPageAndEvent(
              BillingAnalytics.pageSchedulePaymentComplete,
              this.eventAnalytic
            );
          } else {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageAddAutoPayCompleteAdmin,
                  event: this.eventAnalytic
                }
              })
            );
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageSchedulePaymentCompleteAdmin,
                  event: this.eventAnalytic
                }
              })
            );
          }
          break;
        case 'autoPayWithoutPayment':
          if (!this.isAdminApp) {
            this.analyticsFacade.trackPageAndEvent(
              BillingAnalytics.pageAddAutoPayComplete,
              this.eventAutoPayAnalytic
            );
          } else {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageAddAutoPayCompleteAdmin,
                  event: this.eventAutoPayAnalytic
                }
              })
            );
          }

          break;
        case 'paymentWithoutAutoPay':
          if (!this.isAdminApp) {
            this.analyticsFacade.trackPageAndEvent(
              BillingAnalytics.pageSchedulePaymentComplete,
              this.eventAnalytic
            );
          } else {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageSchedulePaymentCompleteAdmin,
                  event: this.eventAnalytic
                }
              })
            );
          }

          break;
        case 'addPaymentMethod':
          if (!this.isAdminApp) {
            this.analyticsFacade.trackPage(BillingAnalytics.pageAddPaymentAccComplete);
          } else {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageAddPaymentAccCompleteAdmin
                }
              })
            );
          }
          break;
        case 'editPaymentMethod':
          if (!this.isAdminApp) {
            this.analyticsFacade.trackPage(BillingAnalytics.pageEditPaymentAccComplete);
          } else {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageEditPaymentAccCompleteAdmin
                }
              })
            );
          }
          break;
        case 'deletePaymentMethod':
          if (!this.isAdminApp) {
            this.analyticsFacade.trackPage(BillingAnalytics.pageDeletePaymentAccComplete);
          } else {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageDeletePaymentAccCompleteAdmin
                }
              })
            );
          }
          break;
        case 'editScheduled':
          if (!this.isAdminApp) {
            this.analyticsFacade.trackPage(BillingAnalytics.pageEditSchedulePaymentComplete);
          } else {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageEditSchedulePaymentCompleteAdmin
                }
              })
            );
          }
          break;
        case 'deleteScheduled':
          if (!this.isAdminApp) {
            this.analyticsFacade.trackPage(BillingAnalytics.pageDeleteSchedulePaymentComplete);
          } else {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: BillingAnalytics.pageDeleteSchedulePaymentCompleteAdmin
                }
              })
            );
          }
          break;
        case 'pastDue':
        case 'pastAndCurrentDue': {
          const analytic = !this.isAdminApp
            ? BillingAnalytics.pageScheduledPaymentConfirmationPastDue
            : BillingAnalytics.pageAdminScheduledPaymentConfirmationPastDue;
          // Check terms and see if they're all the same or mixed
          if (
            this.billingConfirmationData?.paymentTerm &&
            new Set(this.billingConfirmationData.paymentTerm).size === 1
          ) {
            switch (this.billingConfirmationData.paymentTerm[0]) {
              case 'pastDue':
                analytic.paymentAmount = 'Past Due';
                break;
              case 'pastPlusCurrent':
                analytic.paymentAmount = 'Past and Current';
                break;
              default:
                analytic.paymentAmount = 'Account Balance';
                break;
            }
          } else {
            analytic.paymentAmount = 'Mixed Amount';
          }

          if (!this.isAdminApp) {
            this.analyticsFacade.trackPage(analytic);
          } else {
            this.store.dispatch(
              AnalyticsActions.sendAnalytic({
                options: {
                  page: analytic
                }
              })
            );
          }
          break;
        }
        default:
          break;
      }
    }
  }
}
